import React, { useEffect, useRef, useState } from 'react';
import { useMatrix } from '../contexts/MatrixContext';
import './MatrixRain.css';

function MatrixRain() {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const { isTradeTrackerActive } = useMatrix();
  const [faded, setFaded] = useState(false);

  // Fade out after 5 seconds and stop animation to save resources
  useEffect(() => {
    const timer = setTimeout(() => {
      setFaded(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Stop the canvas animation after the CSS fade completes (2s transition)
  useEffect(() => {
    if (!faded) return;
    const stopTimer = setTimeout(() => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    }, 2000);
    return () => clearTimeout(stopTimer);
  }, [faded]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');

    // Set canvas size to window size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Matrix characters - mix of katakana, numbers, and letters
    const chars = "ﾊﾐﾋｰｳｼﾅﾓﾆｻﾜﾂｵﾘｱﾎﾃﾏｹﾒｴｶｷﾑﾕﾗｾﾈｽﾀﾇﾍ01234567890ABCDEFGHIJKLMNOPQRSTUVWXYZ";

    // Font size and columns (increased fontSize = fewer columns)
    const fontSize = 24;
    const columns = Math.floor(canvas.width / fontSize);

    // Array to track y position of each column
    const drops = [];
    for (let i = 0; i < columns; i++) {
      drops[i] = Math.random() * -100; // Start at random positions above screen
    }

    // Frame counter for slower animation
    let frameCount = 0;

    // Animation function
    const draw = () => {
      frameCount++;

      // Only update every 3 frames for slower animation
      if (frameCount % 3 !== 0) {
        animationRef.current = requestAnimationFrame(draw);
        return;
      }

      // Semi-transparent black to create trail effect
      ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Matrix green color with subtle glow
      ctx.fillStyle = '#00FF41';
      ctx.shadowColor = '#00FF41';
      ctx.shadowBlur = 3;
      ctx.font = `${fontSize}px Share Tech Mono, monospace`;

      // Draw characters
      for (let i = 0; i < drops.length; i++) {
        // Random character from the set
        const text = chars[Math.floor(Math.random() * chars.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        // Draw the character
        ctx.fillText(text, x, y);

        // Randomly reset column or move it down
        if (y > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }

        // Move drop down
        drops[i]++;
      }

      // Reset shadow for next frame
      ctx.shadowBlur = 0;

      animationRef.current = requestAnimationFrame(draw);
    };

    // Start animation
    draw();

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`matrix-rain ${faded || isTradeTrackerActive ? 'fade-out' : ''}`}
    />
  );
}

export default MatrixRain;
